import { ArrowRight, Check, Warning } from '@phosphor-icons/react';
import { Link, useLocation } from 'react-router-dom';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { Callout } from '../components/Callout';
import { MediaFrame } from '../components/MediaFrame';
import { Reveal } from '../components/Reveal';
import { ResponsiveImage } from '../components/ResponsiveImage';
import { ServiceProgressRail } from '../components/ServiceProgressRail';
import { getService, services } from '../content/site';

export function ServiceDetail() {
  const { pathname } = useLocation();
  const serviceSlug = pathname.split('/').filter(Boolean)[0];
  const service = getService(serviceSlug);
  if (!service) return null;
  const related = services.filter((item) => item.slug !== service.slug).slice(0, 3);
  const pageSections = [
    { id: 'service-concerns', shortLabel: 'Review' },
    { id: 'service-includes', shortLabel: 'Scope' },
    { id: 'service-approach', shortLabel: 'Approach' },
    ...(service.projectPhotos?.length > 0 ? [{ id: 'service-projects', shortLabel: 'Real work' }] : []),
    { id: 'service-faqs', shortLabel: 'FAQs' },
  ];

  return (
    <>
      <section className="service-hero section-shell">
        <Breadcrumbs items={[{ to: '/services', label: 'Services' }, { label: service.name }]} />
        <div className="service-hero__grid">
          <Reveal className="service-hero__copy">
            <p className="section-kicker">Eastern North Carolina</p>
            <h1>{service.name}</h1>
            <p>{service.intro}</p>
            <Link className="button button--primary" to="/contact">Request an Estimate <ArrowRight aria-hidden="true" size={18} /></Link>
          </Reveal>
          <Reveal className="service-hero__media" delay={0.08} variant="media">
            <MediaFrame className="service-hero__image-frame">
              <ResponsiveImage
                src={service.image}
                alt={service.imageAlt}
                width="1200"
                height="900"
                fetchPriority="high"
                sizes="(max-width: 1023px) calc(100vw - 32px), min(55vw, 730px)"
                style={{ objectPosition: service.imagePosition }}
              />
            </MediaFrame>
          </Reveal>
        </div>
      </section>

      <ServiceProgressRail key={service.slug} label={service.navName} sections={pageSections} />

      <section className="service-concerns section-pad section-shell" id="service-concerns">
        <Reveal><h2>{service.concernsTitle}</h2></Reveal>
        <div className="concern-list">
          {service.concerns.map((concern, index) => (
            <Reveal key={concern} delay={Math.min(index * 0.04, 0.2)}><Warning aria-hidden="true" size={22} weight="duotone" /><span>{concern}</span></Reveal>
          ))}
        </div>
      </section>

      <section className="service-includes section-pad" id="service-includes">
        <div className="section-shell service-includes__grid">
          <Reveal className="service-includes__intro"><h2>Project scopes can include</h2><p>Final recommendations depend on the condition, access, site, and intended use.</p></Reveal>
          <div className="include-list">
            {service.includes.map((item, index) => <Reveal key={item} delay={Math.min(index * 0.04, 0.2)}><Check aria-hidden="true" size={18} weight="bold" /><span>{item}</span></Reveal>)}
          </div>
        </div>
      </section>

      <section className="approach-section section-pad section-shell" id="service-approach">
        <Reveal className="section-heading"><h2>What shapes the recommendation.</h2></Reveal>
        <div className="approach-grid">
          {service.approach.map((item, index) => <Reveal key={item.title} delay={index * 0.08}><h3>{item.title}</h3><p>{item.text}</p></Reveal>)}
        </div>
      </section>

      {service.projectPhotos?.length > 0 && (
        <section className={`service-projects section-pad${service.projectPhotos.some((photo) => photo.orientation === 'portrait') ? ' service-projects--portrait' : ''}`} id="service-projects">
          <div className="section-shell">
            <Reveal className="section-heading">
              <p className="section-kicker">Real project work</p>
              <h2>{service.projectStoryTitle}</h2>
              <p>{service.projectStoryIntro}</p>
            </Reveal>
            <div className="service-projects__grid">
              {service.projectPhotos.map((photo, index) => (
                <Reveal as="figure" key={photo.title} delay={Math.min(index * 0.06, 0.18)} variant="media">
                  <MediaFrame className="service-projects__media">
                    <ResponsiveImage
                      src={photo.image}
                      alt={photo.alt}
                      width={photo.width}
                      height={photo.height}
                      loading="lazy"
                      sizes="(max-width: 520px) calc(100vw - 24px), (max-width: 1023px) calc(50vw - 24px), min(24vw, 320px)"
                    />
                  </MediaFrame>
                  <figcaption><strong>{photo.title}</strong><p>{photo.caption}</p><span>{photo.location}</span></figcaption>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="faq-section section-pad section-shell" id="service-faqs">
        <Reveal className="section-heading"><h2>Common questions about {service.navName.toLowerCase()}.</h2></Reveal>
        <div className="faq-list">
          {service.faqs.map((faq) => <details key={faq.q}><summary>{faq.q}</summary><p>{faq.a}</p></details>)}
        </div>
      </section>

      <section className="related-services section-pad section-shell">
        <Reveal className="section-heading"><h2>Related services</h2></Reveal>
        <div>{related.map((item) => <Link key={item.slug} to={`/${item.slug}`}><span>{item.navName}</span><ArrowRight aria-hidden="true" size={18} /></Link>)}</div>
      </section>

      <Callout title={`Talk through your ${service.navName.toLowerCase()} project.`} />
    </>
  );
}
